import { JsonRecord, ProviderGatewayMode, providerGatewayModeOptions } from './types';
import {
  LLMModel,
  LLMProvider,
  LLMModelInvocationProfileInput,
  LLMInvocationMethod,
} from '../../../types/admin';

/** Task type codes of a model, primary first; falls back to the single primary code. */
export function modelTaskTypeCodes(model: LLMModel): string[] {
  if (model.taskTypeCodes && model.taskTypeCodes.length > 0) return model.taskTypeCodes;
  return model.taskTypeCode ? [model.taskTypeCode] : [];
}

export function toPositiveInteger(value?: number) {
  return value && Number.isInteger(value) && value > 0 ? value : undefined;
}

export function normalizeOptionalJsonForSave(value: string | undefined, clearAsNull: boolean) {
  const trimmed = value?.trim();
  if (trimmed) return trimmed;
  return clearAsNull ? null : undefined;
}

export function parseJsonRecord(raw?: string): { value: JsonRecord; error?: string } {
  if (!raw?.trim()) {
    return { value: {} };
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return { value: {}, error: 'Config must be a JSON object' };
    }

    return { value: parsed as JsonRecord };
  } catch {
    return { value: {}, error: 'Config must be valid JSON' };
  }
}

export function stringifyJsonRecord(config: JsonRecord) {
  const cleaned = Object.fromEntries(
    Object.entries(config).filter(([, value]) => value !== undefined && value !== '')
  );

  return Object.keys(cleaned).length ? JSON.stringify(cleaned, null, 2) : '';
}

export function getConfigString(config: JsonRecord, key: string) {
  const value = config[key];
  return typeof value === 'string' ? value : '';
}

export function getProviderGatewayMode(config: JsonRecord): ProviderGatewayMode {
  if (config.gatewayMode === ProviderGatewayMode.ProviderNative) {
    return ProviderGatewayMode.ProviderNative;
  }

  if (config.gatewayMode === ProviderGatewayMode.RestApi) {
    return ProviderGatewayMode.RestApi;
  }

  if (getConfigString(config, 'customProviderSlug')) {
    return ProviderGatewayMode.ProviderNative;
  }

  return ProviderGatewayMode.Auto;
}

export function getGatewayModeDescription(mode: ProviderGatewayMode) {
  return providerGatewayModeOptions.find((option) => option.value === mode)?.description ?? '';
}

export function synthesizeLegacyProfiles(
  model: LLMModel,
  providers: LLMProvider[]
): LLMModelInvocationProfileInput[] {
  let requestFormat: string | undefined;
  if (model.config) {
    try {
      const parsed = typeof model.config === 'string' ? JSON.parse(model.config) : model.config;
      if (parsed && typeof parsed.requestFormat === 'string') {
        requestFormat = parsed.requestFormat.trim().toLowerCase();
      }
    } catch {
      // ignore
    }
  }

  let method: LLMInvocationMethod = 'cloudflare_run';

  if (requestFormat === 'run') {
    method = 'cloudflare_run';
  } else if (requestFormat === 'chat-completions') {
    method = 'openai_chat_completions';
  } else if (requestFormat === 'responses') {
    method = 'openai_responses';
  } else if (requestFormat === 'messages') {
    method = 'anthropic_messages';
  } else if (model.code.startsWith('@cf/')) {
    method = 'ai_binding';
  } else {
    const provider = model.provider ?? providers.find((p) => p.publicId === model.providerPublicId);
    const providerCode = provider?.code?.toLowerCase() || '';
    const taskCode = (model.taskTypeCode || model.taskType?.code || '').toLowerCase();
    const modelCode = model.code.toLowerCase();

    if (providerCode === 'anthropic' || modelCode.includes('claude')) {
      method = 'anthropic_messages';
    } else if (
      providerCode === 'openai' ||
      modelCode.includes('gpt') ||
      modelCode.includes('o1') ||
      modelCode.includes('o3') ||
      taskCode === 'text_generation' ||
      taskCode === 'chat' ||
      taskCode === 'text-generation'
    ) {
      method = 'openai_chat_completions';
    } else {
      method = 'cloudflare_run';
    }
  }

  const supportsTools =
    method === 'openai_chat_completions' ||
    method === 'openai_responses' ||
    method === 'anthropic_messages';
  const supportsJsonMode = method === 'openai_chat_completions' || method === 'openai_responses';
  const supportsReasoning =
    method === 'openai_chat_completions' ||
    method === 'openai_responses' ||
    method === 'anthropic_messages';

  return [
    {
      key: method,
      method,
      priority: 100,
      modes: ['cheap', 'normal', 'expensive'],
      supportsStream: true,
      supportsTools,
      supportsJsonMode,
      supportsReasoning,
      isDefault: true,
      isActive: true,
    },
  ];
}
